import { Button, colors, Icon, Modal, Text } from "@thetreep/ui";
import { ChangeEvent, DragEvent, useRef, useState } from "react";
import { importDAP } from "@services/dap";
import FileIcon from "@/assets/icons/file.svg";
import { ProgressBar } from "@components/common/ProgressBar";
import { ProgressCircle } from "@components/common/ProgressCircle";
import { ImportResponse } from "@gen/dap/v1/dap_pb";
import { useTranslation } from "react-i18next";
import { ConnectError } from "@connectrpc/connect";
import theme from "@theme";

interface UploadState {
  name: string;
  totalBytes: number;
  sentBytes: number;
  done: boolean;
  response?: ImportResponse;
  error?: ConnectError;
}

interface DAPImportProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: () => void;
}

export function DAPImport({ isOpen, onClose, onImport }: DAPImportProps) {
  const uploadButton = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<UploadState[]>([]);
  const { t } = useTranslation();

  const setCurrentFileProperties = (file: Partial<UploadState>) => {
    setFiles((prevFiles) => {
      const fileIndex = prevFiles.findIndex((f) => f.name === file.name);
      if (fileIndex === -1) return prevFiles;

      const updatedFiles = [...prevFiles];
      updatedFiles[fileIndex] = { ...updatedFiles[fileIndex], ...file };
      return updatedFiles;
    });
  };

  const addNewFile = (file: UploadState) => {
    setFiles((prevFiles) => [file, ...prevFiles]);
  };

  const onUpload = (file: File) => {
    addNewFile({
      totalBytes: file.size,
      sentBytes: 0,
      done: false,
      name: file.name,
    });

    (async () => {
      try {
        const response = await importDAP(file, (b) => {
          setCurrentFileProperties({
            name: file.name,
            totalBytes: file.size,
            sentBytes: b,
          });
        });
        setCurrentFileProperties({
          response: response,
          name: file.name,
          done: true,
        });
        onImport();
      } catch (e: unknown) {
        console.error("Error uploading DAP file", e);
        const connectErr = ConnectError.from(e);
        setCurrentFileProperties({ name: file.name, error: connectErr });
      }
    })();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const file = event.target.files[0];
      onUpload(file);
    }
  };

  const onDrop = (e: DragEvent<HTMLInputElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    onUpload(file);
  };

  const onDragOver = (e: DragEvent<HTMLInputElement>) => {
    e.stopPropagation();
    e.preventDefault();
  };

  /**
   * fileProgress will display the progress of the upload of each file.
   */
  const fileProgress = () => {
    const filesToDisplay = files.filter((f) => !f.error);
    if (filesToDisplay.length === 0) {
      return (
        <Text color="neutral500" center>
          {t("admin.dap.import.noFileSelected")}
        </Text>
      );
    }
    return filesToDisplay.map((file, index) => {
      const progress =
        file.totalBytes && file.sentBytes
          ? Math.round((file.sentBytes / file.totalBytes) * 100)
          : 0;
      return (
        <div
          key={file.name}
          className="flex items-center justify-center w-full gap-2"
        >
          <img
            src={FileIcon}
            alt={t("admin.dap.import.fileIconAlt")}
            className="w-12 h-12"
          />
          <div className="flex-1 flex flex-col gap-1">
            <Text size="s">{file.name}</Text>
            {progress < 100 && (
              <>
                <ProgressBar
                  progress={progress}
                  key={index}
                  height={6}
                  fillColor={colors.secondary}
                />
                <div className="flex justify-between">
                  <Text size="s">
                    {t("admin.dap.import.uploadProgress", {
                      sent: readableFileSize(file.sentBytes),
                      total: readableFileSize(file.totalBytes),
                    })}
                  </Text>
                  <Text size="s">
                    {t("admin.dap.import.uploadPercentage", {
                      percent: computePercent(file.sentBytes, file.totalBytes),
                    })}
                  </Text>
                </div>
              </>
            )}
          </div>
          {progress >= 100 && (
            <Icon
              src="check_circle_line"
              color={colors.successStrong}
              size={24}
            />
          )}
        </div>
      );
    });
  };

  function selectMessage() {
    return (
      <>
        <Icon src="align_arrow_up_line" size={82} />
        <Text bold>{t("admin.dap.import.dragDropInstruction")}</Text>
        <Text>{t("admin.dap.import.or")}</Text>
        <Button
          data-testid="select-dap-file-button"
          kind="primary"
          colors={{
            backgroundColor: theme.colors.cnam.blue.DEFAULT,
            color: theme.colors.white,
          }}
          onClick={() => uploadButton.current?.click()}
        >
          {t("admin.dap.import.selectFile")}
        </Button>
      </>
    );
  }

  function errorMessage() {
    const error = files[0].error;
    if (!error) return;
    return (
      <>
        <Icon src="close_circle_fill" color={colors.errorStrong} size={82} />
        <div className="flex flex-col items-center m-4 text-center">
          <Text data-testid="upload-error-message">
            {t("admin.dap.import.uploadError", {
              count: files[0].response?.errorSummary?.errors.length || 0,
            })}
          </Text>
          <Text>{error.rawMessage}</Text>
        </div>
        <Button
          data-testid="select-dap-file-button"
          kind="outline"
          colors={{
            backgroundColor: theme.colors.cnam.blue.DEFAULT,
            color: theme.colors.cnam.blue.DEFAULT,
          }}
          onClick={() => uploadButton.current?.click()}
        >
          {t("admin.dap.import.selectAnotherFile")}
        </Button>
      </>
    );
  }

  function warningMessage() {
    const importResponse = files[0].response;
    return (
      <>
        <Icon src="information_line" color={colors.warningStrong} size={82} />
        <div>
          <Text data-testid="upload-warning-message">
            {t("admin.dap.import.uploadComplete")}
          </Text>
          <Text data-testid="upload-warning-message">
            {t("admin.dap.import.uploadCount", {
              count: importResponse?.total,
            })}
          </Text>
          <div className="h-2" />
          <Text data-testid="upload-warning-message">
            {t("admin.dap.import.uploadSuccess", {
              count: importResponse?.validCount,
            })}
          </Text>
          <Text data-testid="upload-warning-message">
            {t("admin.dap.import.uploadUpdated", {
              count: importResponse?.updatedCount,
            })}
          </Text>
          <Text data-testid="upload-warning-message">
            {t("admin.dap.import.uploadIgnored", {
              count: importResponse?.ignoredCount,
            })}
          </Text>
          <Text data-testid="upload-warning-message">
            {t("admin.dap.import.uploadErrors", {
              count: importResponse?.errorCount,
            })}
          </Text>
        </div>
        <ul className="max-h-40 w-[90%] m-2 p-2 overflow-auto bg-cnam-light-100 rounded-lg">
          {importResponse?.errorSummary?.errors.map(
            (err: { lineNumber: number; reasons: string[] }, idx: number) => (
              <li key={idx}>
                <strong>
                  {t("admin.dap.import.uploadErrorLine", {
                    count: err.lineNumber,
                  })}
                </strong>
                <ul className=" ml-4 list-disc">
                  {err.reasons.map((reason: string, reasonIdx: number) => (
                    <li key={reasonIdx} className=" font-robot text-sm">
                      {reason.charAt(0).toUpperCase() + reason.slice(1)}
                    </li>
                  ))}
                </ul>
              </li>
            ),
          )}
        </ul>
        <Button
          data-testid="select-dap-file-button"
          kind="outline"
          colors={{
            backgroundColor: theme.colors.cnam.blue.DEFAULT,
            color: theme.colors.cnam.blue.DEFAULT,
          }}
          onClick={() => uploadButton.current?.click()}
        >
          {t("admin.dap.import.selectAnotherFile")}
        </Button>
      </>
    );
  }

  function validMessage() {
    return (
      <>
        <Icon src="check_circle_line" color={colors.successStrong} size={82} />
        <Text data-testid="upload-success-message">
          {t("admin.dap.import.uploadComplete")}
        </Text>
        <Button
          data-testid="select-dap-file-button"
          kind="outline"
          colors={{
            backgroundColor: theme.colors.cnam.blue.DEFAULT,
            color: theme.colors.cnam.blue.DEFAULT,
          }}
          onClick={() => uploadButton.current?.click()}
        >
          {t("admin.dap.import.selectAnotherFile")}
        </Button>
      </>
    );
  }

  function progressMessage() {
    return (
      <>
        <ProgressCircle
          progress={computePercent(files[0].sentBytes, files[0].totalBytes)}
          fillColor="cnam"
        />
        <Text>{t("admin.dap.import.uploading")}</Text>
        <Button kind="outline" onClick={() => uploadButton.current?.click()}>
          {t("admin.dap.import.cancel")}
        </Button>
      </>
    );
  }

  function innerElement() {
    if (files.length === 0) {
      return selectMessage();
    }
    if (files[0].error) {
      return errorMessage();
    }
    if (files[0].done) {
      const importResponse = files[0].response;
      if (
        (importResponse?.errorCount && importResponse?.errorCount > 0) ||
        (importResponse?.ignoredCount && importResponse?.ignoredCount > 0)
      ) {
        return warningMessage();
      }
      return validMessage();
    }
    return progressMessage();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setFiles([]) ?? onClose()}
      content={{ title: t("admin.dap.import.modalTitle") }}
      size="default"
    >
      <div className="w-full h-full flex flex-col justify-center items-center gap-4">
        <div
          className="
                    flex flex-col items-center justify-center
                    border-neutral-400 border-2 rounded-2xl border-dashed 
                    py-8 w-full gap-2 z-50
                "
          onDrop={onDrop}
          onDragOver={onDragOver}
        >
          {innerElement()}
        </div>
        {fileProgress()}
      </div>
      <input
        data-testid="dap-file-input"
        ref={uploadButton}
        className="hidden"
        type="file"
        accept="text/csv,.xlsx,.xls"
        onChange={handleFileChange}
      />
    </Modal>
  );
}

/**
 * Converts a number of bytes into a human-readable string with appropriate units
 * @param bytes - The number of bytes to format
 * @param decimals - Number of decimal places to show (default: 2)
 * @returns Formatted string with appropriate unit (B, KB, MB, GB, TB, PB)
 * @example
 * readableFileSize(1234) // "1.21 KB"
 * readableFileSize(1234567) // "1.18 MB"
 * readableFileSize(1234567890) // "1.15 GB"
 */
function readableFileSize(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return "0 B";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["B", "KB", "MB", "GB", "TB", "PB"];

  // Calculate the exponent to determine the appropriate unit
  // Math.floor(Math.log(bytes) / Math.log(k)) gives us the power of 1024 needed
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // Convert to the appropriate unit and round to specified decimals
  const value = bytes / Math.pow(k, i);

  return `${parseFloat(value.toFixed(dm))} ${sizes[i]}`;
}

/**
 * Computes a percentage value, ensuring it is within the range of 0 to 100.
 * Rounds the result to a maximum of 2 decimal places.
 *
 * @param value - The current value to compute the percentage for.
 * @param total - The total or maximum value to base the percentage on.
 * @returns The computed percentage, constrained to the range of 0 to 100, rounded to 2 decimal places.
 *          Returns 0 if the input values are invalid.
 */
function computePercent(value: number, total: number): number {
  // Validate inputs
  if (total <= 0 || value < 0) {
    return 0;
  }

  // Calculate percentage
  const percent = (value / total) * 100;

  // Constrain the result to the range [0, 100] and round to 2 decimal places
  return Math.min(100, Math.max(0, Math.round(percent * 100) / 100));
}
